import { Slider, SliderItem } from "gamfi/components/slider/Slider";
import ProgressBar from "gamfi/components/progressBar";
import CountDown from "gamfi/components/countDown";
import CardHover from "gamfi/components/cardHover";
import LiveProjectStyleWrapper from "gamfi/sections/Projects/v2/LiveProject.style";
import IconAntimatter from "assets/tokens/icon-antimatter-200.png";
import IconMatter from "assets/tokens/icon-matter-200.png";
import Button from "gamfi/components/button";
import { metaverse } from "data/metaverse";
import { now } from "utils/time";
import { formatEther, formatter, percent } from "utils/math";
import { useCallback, useContext } from "react";
import { MetaverseContext } from "blockchain/provider";
import { useFetch } from "utils/hooks";
import { useSearchParams } from "react-router-dom";
import { useWeb3Modal } from "@web3modal/react";
import {useAccount} from "wagmi";

const settings = {
  showTitle: true,
  size: 30,
  labelSize: 20,
  direction: "right",
  backgroundColor: "transparent",
  color: "#fff",
  dayTitle: "D",
  hourTitle: "H",
  minuteTitle: "M",
  secondTitle: "S",
  id: "countdownwrap",
};

const sliderSettings = {
  dots: true,
  arrows: false,
  autoplay: true,
  speed: 2000,
  autoplaySpeed: 4000,
  cssEase: "linear",
  centerMode: true,
  centerPadding: "0px",
  infinite: true,
  slidesToShow: 1,
  slidesToScroll: 1,
  className: ``,
};

interface ItemProps {
  coinName: string;
  coinSymbol: string;
  coinIcon: string;

  saleEnd: number;

  totalSupply: number;
  allocation: number;
  totalRise: number;

  onClaim: () => void;
  onAddToken: () => void;

  hasClaimed?: boolean;
  loading?: boolean;
}

function Item(props: ItemProps): JSX.Element {
  const { isConnected } = useAccount();
  const handleClaim = useCallback(
    (e: any) => {
      e.preventDefault();
      props.onClaim();
    },
    [props]
  );

  const handleAddToken = useCallback(
    (e: any) => {
      e.preventDefault();
      props.onAddToken();
    },
    [props]
  );

  const { open } = useWeb3Modal();
  const handleOpenModal = useCallback(
    (e: any) => {
      e.preventDefault();
      open();
    },
    [open]
  );

  const handleLoading = useCallback((e: any) => e.preventDefault(), []);

  const claimPercent = percent(props.allocation, props.totalRise).toFixed(2) + "%";
  const format = formatter(props.coinSymbol);

  const totalClaimed = `${format.format(props.totalRise)} ( ${claimPercent} )`;

  const disconnectedButton = props.loading ? (
    <Button href={`#`} onClick={handleLoading}>
      LOADING
    </Button>
  ) : (
    <Button href={`#`} onClick={handleOpenModal}>
      CONNECT
    </Button>
  );

  const claimButton = props.hasClaimed ? (
    <Button href={`#`} onClick={handleAddToken}>
      ADD TOKEN
    </Button>
  ) : (
    <Button href={`#`} onClick={handleClaim}>
      CLAIM NOW
    </Button>
  );

  return (
    <SliderItem className={``}>
      <div className="game-price-item">
        <div className="game-price-inner">
          <div className="total-price">
            <div className="price-inner d-flex mb-45 md-mb-20">
              <div className="price-details">
                <h3 className="mb-15">
                  <a href="/projects-details-1">
                    {props.coinName} ( {props.coinSymbol} )
                  </a>
                </h3>
                <div className="dsc">Max Total Supply {format.format(props.totalSupply)}</div>
              </div>
            </div>
            <div className="all-raise">Total Claimed {props.loading ? "Loading..." : totalClaimed}</div>
          </div>
          <div className="allocation-max text-center">
            <img src={props.coinIcon} alt="icon" />
            <div className="allocation">Allocation {format.format(props.allocation)}</div>
          </div>
          <div className="targeted-raise">
            <div className="seles-end-text">Big Bang will occur within</div>
            <CountDown {...settings} count={props.saleEnd} />
            <div className="targeted-raise-amount">
              {props.loading || !isConnected ? disconnectedButton : claimButton}
            </div>
          </div>
        </div>
        <div className="progress-inner">
          <ProgressBar progress={claimPercent} />
        </div>

        {/* hover */}
        <CardHover />
      </div>
    </SliderItem>
  );
}

const LiveProject = () => {
  const [searchParams] = useSearchParams();
  const referral = searchParams.get("ref") || metaverse.owner;

  const { address, isConnected } = useAccount();

  const { antimatter, matter } = useContext(MetaverseContext);

  const fetchData = useCallback(async () => {
    const data = await Promise.all([antimatter.balanceOf(metaverse.antimatter), matter.balanceOf(metaverse.matter)]);

    const result = {
      antimatterRise: metaverse.antimatterAirdrop - formatEther(data[0]),
      matterRise: metaverse.matterAirdrop - formatEther(data[1]),
      hasClaimedMatter: false,
      hasClaimedAntimatter: false,
    };

    if (isConnected && address) {
      const balances = await Promise.all([antimatter.balanceOf(address), matter.balanceOf(address)]);
      result.hasClaimedAntimatter = balances[0] > 0;
      result.hasClaimedMatter = balances[1] > 0;
    }
    
    return result;
  }, [antimatter, matter, isConnected, address]);

  const { data, loading, error } = useFetch(fetchData);

  if (error) {
    console.log({ error });
  }

  return (
    <LiveProjectStyleWrapper className="live_project_wrapper">
      <div className="container">
        <Slider {...sliderSettings}>
          <Item
            coinName="Matter Token"
            coinSymbol="BMA"
            coinIcon={IconMatter}
            saleEnd={metaverse.time - now()}
            allocation={metaverse.matterAirdrop}
            totalSupply={metaverse.matterSupply}
            totalRise={data?.matterRise || 0}
            onClaim={() => matter.claim(referral)}
            onAddToken={() => matter.addToken("BMA", "https://metaverse-energy.github.io/icon-matter.jpg")}
            hasClaimed={data?.hasClaimedMatter}
            loading={loading}
          />
          <Item
            coinName="Antimatter Token"
            coinSymbol="BAM"
            coinIcon={IconAntimatter}
            saleEnd={metaverse.time - now()}
            allocation={metaverse.antimatterAirdrop}
            totalSupply={metaverse.antimatterSupply}
            totalRise={data?.antimatterRise || 0}
            onClaim={() => antimatter.claim(referral)}
            onAddToken={() => antimatter.addToken("BAM", "https://metaverse-energy.github.io/icon-antimatter.jpg")}
            hasClaimed={data?.hasClaimedAntimatter}
            loading={loading}
          />
        </Slider>
      </div>
    </LiveProjectStyleWrapper>
  );
};

export default LiveProject;
